import React, { useCallback, useEffect, useMemo, useState } from "react";
import { TailSpin } from "react-loader-spinner";
import { BiExport } from "react-icons/bi";

import AdminTransactionsExportModal from "../../components/admin-export/AdminTransactionsExportModal";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import { DataTable } from "../../components/data-table";
import { PROD_LINK, STAGING_LINK } from "../../constant";
import { IS_STAGING } from "../../constant/api";
import {
  DEFAULT_OTC_SUBTYPE,
  OTC_SUBTYPE_FILTERS,
  type OtcSubtypeFilterValue,
} from "../../lib/api/adminOtcSubtypes";
import { useAdminOtcTransactionSummariesQuery } from "../../lib/api/useAdminOtcTransactionSummariesQuery";
import { useAdminOtcTransactionsDataQuery } from "../../lib/api/useAdminOtcTransactionsDataQuery";
import type { AdminOtcCurrencyDistributionItem } from "../../lib/api/adminOtcTransactionSummaries";
import { formatAmount } from "../../utils/FormatPerson";
import OtcDetailModal from "../orders/OtcDetailModal";
import {
  buildDeexDeskOtcTableRows,
  createDeexDeskOtcTableColumns,
  mapOtcTransactionForModal,
  type DeexDeskOtcTableRow,
} from "./deexDeskOtcTable";

const SEGMENT_COLORS = [
  "#0D851D",
  "#BE6B0A",
  "#8B5CF6",
  "#279DF3",
  "#D92B2B",
  "#4E606E",
  "#EAB308",
  "#EC4899",
  "#14B8A6",
  "#F97316",
];

const TOP_CURRENCIES = 6;

type MergedOtcCurrency = {
  currency: string;
  usdAmount: number;
  count: number;
  amount: number;
  ngnAmount: number;
};

function mergeOtcCurrencyDistribution(
  items: AdminOtcCurrencyDistributionItem[]
): MergedOtcCurrency[] {
  const map = new Map<string, MergedOtcCurrency>();
  for (const item of items) {
    const key = item.currency.toUpperCase();
    const prev = map.get(key);
    const addUsd = Number(item.usdAmount) || 0;
    const addCount = Number(item.count) || 0;
    const addAmount = Number(item.amount) || 0;
    const addNgn = Number(item.ngnAmount) || 0;
    if (prev) {
      prev.usdAmount += addUsd;
      prev.count += addCount;
      prev.amount += addAmount;
      prev.ngnAmount += addNgn;
    } else {
      map.set(key, {
        currency: key,
        usdAmount: addUsd,
        count: addCount,
        amount: addAmount,
        ngnAmount: addNgn,
      });
    }
  }
  return Array.from(map.values());
}

function buildBarRows(merged: MergedOtcCurrency[]): MergedOtcCurrency[] {
  const sorted = [...merged].sort((a, b) => b.usdAmount - a.usdAmount);
  const top = sorted.slice(0, TOP_CURRENCIES);
  const rest = sorted.slice(TOP_CURRENCIES);
  const restUsd = rest.reduce((s, r) => s + r.usdAmount, 0);
  if (restUsd <= 0) return top;
  return [
    ...top,
    {
      currency: "Other",
      usdAmount: restUsd,
      count: rest.reduce((s, r) => s + r.count, 0),
      amount: rest.reduce((s, r) => s + r.amount, 0),
      ngnAmount: rest.reduce((s, r) => s + r.ngnAmount, 0),
    },
  ];
}

type OtcCurrencySegment = MergedOtcCurrency & {
  pct: number;
  color: string;
  showLabel: boolean;
};

function formatStatusLabel(key: string): string {
  if (key === "COMEPLETED") return "Completed";
  return key
    .toLowerCase()
    .split(/_/g)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function statusAccent(key: string): { bar: string; glow: string } {
  const u = key.toUpperCase();
  if (u === "COMPLETED" || u === "SUCCESSFUL" || u === "COMEPLETED") {
    return { bar: "#03C668", glow: "rgba(3, 198, 104, 0.12)" };
  }
  if (u === "CONFIRMED") {
    return { bar: "#14B8A6", glow: "rgba(20, 184, 166, 0.12)" };
  }
  if (u === "FAILED") {
    return { bar: "#DF4E4E", glow: "rgba(223, 78, 78, 0.12)" };
  }
  if (u === "PENDING" || u === "PROCESSING") {
    return { bar: "#F28A0F", glow: "rgba(242, 138, 15, 0.12)" };
  }
  if (u === "HOLD") {
    return { bar: "#869AA9", glow: "rgba(134, 154, 169, 0.12)" };
  }
  return { bar: "#0B75C2", glow: "rgba(11, 117, 194, 0.12)" };
}

function humanizeSubtypeKey(key: string): string {
  const k = key.trim();
  if (!k) return "Unspecified";
  return k
    .split(/[-_]/g)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

function subtypeAccent(key: string): { bar: string; glow: string } {
  const lower = key.toLowerCase();
  if (lower.includes("deposit")) {
    return { bar: "#03C668", glow: "rgba(3, 198, 104, 0.12)" };
  }
  if (lower.includes("payout") || lower.includes("withdraw")) {
    return { bar: "#279DF3", glow: "rgba(39, 157, 243, 0.12)" };
  }
  return { bar: "#8B5CF6", glow: "rgba(139, 92, 246, 0.14)" };
}

const OTC_TABLE_LIMIT = 25;

const DeexDesk: React.FC = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [exportOpen, setExportOpen] = useState(false);
  const [tablePage, setTablePage] = useState(1);
  const [tableSearch, setTableSearch] = useState("");
  const [otcSubtype, setOtcSubtype] =
    useState<OtcSubtypeFilterValue>(DEFAULT_OTC_SUBTYPE);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailTransaction, setDetailTransaction] = useState<Record<
    string,
    unknown
  > | null>(null);

  const dateRangeInvalid = Boolean(startDate && endDate) && startDate > endDate;

  const queryDates = useMemo(() => {
    if (startDate && endDate && !dateRangeInvalid) {
      return { startDate, endDate };
    }
    return {};
  }, [startDate, endDate, dateRangeInvalid]);

  const { data, isPending, isError, error, refetch, isFetching } =
    useAdminOtcTransactionSummariesQuery({
      ...queryDates,
      enabled: !dateRangeInvalid,
    });

  useEffect(() => {
    setTablePage(1);
  }, [startDate, endDate, tableSearch, otcSubtype, dateRangeInvalid]);

  const {
    data: tablePayload,
    isPending: tablePending,
    isError: tableError,
    error: tableQueryError,
    refetch: refetchTable,
    isFetching: tableFetching,
  } = useAdminOtcTransactionsDataQuery({
    page: tablePage,
    search: tableSearch,
    limit: OTC_TABLE_LIMIT,
    ...queryDates,
    otcSubtype,
    enabled: !dateRangeInvalid,
  });

  const pagination = tablePayload?.pagination;

  const tableRows = useMemo(
    () => buildDeexDeskOtcTableRows(tablePayload?.transactions ?? []),
    [tablePayload?.transactions]
  );

  const openOtcDetail = useCallback((row: DeexDeskOtcTableRow) => {
    setDetailTransaction(mapOtcTransactionForModal(row.raw));
    setDetailOpen(true);
  }, []);

  const otcColumns = useMemo(
    () => createDeexDeskOtcTableColumns(openOtcDetail),
    [openOtcDetail]
  );

  const tableTotalPages = Math.max(1, pagination?.totalPages ?? 1);
  const tableTotalCount = pagination?.totalCount ?? 0;

  const barRows = useMemo(() => {
    if (!data?.currencyDistribution?.length) return [];
    const merged = mergeOtcCurrencyDistribution(data.currencyDistribution);
    return buildBarRows(merged);
  }, [data?.currencyDistribution]);

  const segments = useMemo((): OtcCurrencySegment[] => {
    if (!data || barRows.length === 0) return [];
    const barTotal = barRows.reduce((sum, row) => sum + row.usdAmount, 0);
    if (barTotal <= 0) return [];
    return barRows.map((row, i) => {
      const pct = (row.usdAmount / barTotal) * 100;
      return {
        ...row,
        pct,
        color: SEGMENT_COLORS[i % SEGMENT_COLORS.length],
        showLabel: pct >= 5,
      };
    });
  }, [barRows, data]);

  const [selectedCurrency, setSelectedCurrency] = useState<string | null>(null);

  useEffect(() => {
    if (segments.length === 0) {
      setSelectedCurrency(null);
      return;
    }
    setSelectedCurrency((prev) =>
      prev && segments.some((s) => s.currency === prev)
        ? prev
        : segments[0].currency
    );
  }, [segments]);

  const selectedSegment = useMemo(() => {
    if (segments.length === 0) return null;
    return segments.find((s) => s.currency === selectedCurrency) ?? segments[0];
  }, [segments, selectedCurrency]);

  const statusEntries = useMemo(() => {
    if (!data?.statusBreakdown) return [];
    return Object.entries(data.statusBreakdown).sort((a, b) => b[1] - a[1]);
  }, [data?.statusBreakdown]);

  const subtypeEntries = useMemo(() => {
    const raw = data?.subtypeBreakdown;
    if (!raw || typeof raw !== "object") return [];
    return Object.entries(raw).sort((a, b) => b[1] - a[1]);
  }, [data?.subtypeBreakdown]);

  const subtypeTotal = useMemo(
    () => subtypeEntries.reduce((s, [, n]) => s + n, 0),
    [subtypeEntries]
  );

  return (
    <div className="w-full h-screen flex flex-col overflow-hidden">
      <div className="w-full h-[31px] shrink-0 bg-[#1B1B1B] flex justify-center items-center">
        {IS_STAGING ? (
          <h4 className="font-medium text-[#AEBCC6] text-[12px]">
            You are in Staging:{" "}
            <span>
              <a href={PROD_LINK} className="text-[#03C668]">
                Switch to Prod
              </a>
            </span>
          </h4>
        ) : (
          <h4 className="font-medium text-[#AEBCC6] text-[12px]">
            You are in PROD:{" "}
            <span>
              <a href={STAGING_LINK} className="text-[#DF4E4E]">
                Switch to Staging
              </a>
            </span>
          </h4>
        )}
      </div>
      <div className="flex flex-1 min-h-0 w-full flex-col lgss:flex-row overflow-hidden">
        <Sidebar />
        <div className="w-full lgss:w-10/12 flex-1 min-h-0 min-w-0 flex flex-col overflow-y-auto bg-[#13181B]">
          <Navbar />
          <div className="flex-1 px-5 pt-6 pb-24 mds:px-8 lgss:px-10 font-manrope bg-[#13181B] max-w-[1600px] mx-auto w-full">
            <header className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between mb-8">
              <div>
                <h1 className="font-sora font-semibold text-white text-xl tracking-tight">
                  Deex Desk
                </h1>
                <p className="text-[13px] text-[#869AA9] mt-1 max-w-xl leading-relaxed">
                  OTC desk summaries: volume, currency mix, status, and
                  subtypes. Leave dates empty for all-time totals.
                </p>
              </div>

              <div
                className="rounded-xl border border-[#2C363E] bg-[#161E25]/80 p-4 shadow-[0_4px_24px_rgba(0,0,0,0.25)] w-full lg:w-auto lg:min-w-[320px]"
                role="group"
                aria-label="Date range filter"
              >
                <p className="text-[11px] font-semibold text-[#869AA9] uppercase tracking-wider mb-3">
                  Date range
                </p>
                <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
                  <div className="flex-1 min-w-0">
                    <label
                      htmlFor="deex-desk-summary-start"
                      className="block text-[11px] text-[#5C6B76] mb-1"
                    >
                      From
                    </label>
                    <input
                      id="deex-desk-summary-start"
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full h-10 rounded-lg border border-[#2C363E] bg-[#1C2227] px-3 text-[13px] text-[#E7EBEE] outline-none focus:border-secondary_button focus:ring-1 focus:ring-secondary_button/40 transition shadow-inner"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <label
                      htmlFor="deex-desk-summary-end"
                      className="block text-[11px] text-[#5C6B76] mb-1"
                    >
                      To
                    </label>
                    <input
                      id="deex-desk-summary-end"
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full h-10 rounded-lg border border-[#2C363E] bg-[#1C2227] px-3 text-[13px] text-[#E7EBEE] outline-none focus:border-secondary_button focus:ring-1 focus:ring-secondary_button/40 transition shadow-inner"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setStartDate("");
                      setEndDate("");
                    }}
                    className="h-10 shrink-0 px-4 rounded-lg border border-[#2C363E] text-[12px] font-semibold text-[#AEBCC6] hover:text-white hover:bg-[#273037] hover:border-[#3A4852] transition active:scale-[0.98]"
                  >
                    Reset
                  </button>
                </div>
                {(startDate || endDate) && !(startDate && endDate) && (
                  <p className="mt-2 text-[11px] text-[#5C6B76]">
                    Select both dates to filter; otherwise all-time data is
                    shown.
                  </p>
                )}
              </div>
            </header>

            {dateRangeInvalid && (
              <div
                className="rounded-xl border border-amber-500/35 bg-amber-500/[0.07] px-5 py-8 text-center mb-6"
                role="alert"
              >
                <p className="text-[14px] font-semibold text-amber-200/95">
                  Invalid date range
                </p>
                <p className="text-[13px] text-[#AEBCC6] mt-2 max-w-md mx-auto leading-relaxed">
                  Choose an end date on or after the start date, or reset the
                  filter to view summaries.
                </p>
              </div>
            )}

            {!dateRangeInvalid && isPending && (
              <div
                className="flex flex-col items-center justify-center py-24 gap-4 rounded-xl border border-[#2C363E] bg-[#161E25]/40"
                aria-busy="true"
                aria-label="Loading summaries"
              >
                <TailSpin color="#0B75C2" height={40} width={40} />
                <p className="text-[13px] text-[#869AA9]">Loading summaries…</p>
              </div>
            )}

            {!dateRangeInvalid && isError && (
              <div className="rounded-xl border border-red-500/35 bg-red-500/[0.08] px-4 py-4 text-[13px] text-red-100/95 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <span>
                  {error?.response?.data &&
                  typeof error.response.data === "object" &&
                  "message" in error.response.data
                    ? String(
                        (error.response.data as { message?: string }).message
                      )
                    : "Could not load summaries."}
                </span>
                <button
                  type="button"
                  onClick={() => refetch()}
                  className="shrink-0 rounded-lg bg-red-500/20 px-4 py-2 text-[12px] font-semibold text-red-200 border border-red-500/40 hover:bg-red-500/30 transition"
                >
                  Retry
                </button>
              </div>
            )}

            {!dateRangeInvalid && !isPending && !isError && data && (
              <>
                {isFetching && !isPending && (
                  <div className="mb-4 flex items-center gap-2 text-[12px] text-[#869AA9]">
                    <span
                      className="inline-block h-3 w-3 rounded-full border-2 border-[#869AA9] border-t-transparent animate-spin"
                      aria-hidden
                    />
                    Refreshing…
                  </div>
                )}

                <section
                  className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6"
                  aria-label="Key metrics"
                >
                  {[
                    {
                      label: "Total transactions",
                      value: data.totalCount.toLocaleString(),
                      hint: "Count in range",
                    },
                    {
                      label: "Volume (USD)",
                      value: `$${formatAmount(data.totalAmountUSD)}`,
                      hint: "Notional",
                    },
                    {
                      label: "Volume (NGN)",
                      value: `₦${formatAmount(data.totalAmountNGN)}`,
                      hint: "Fiat equivalent",
                    },
                  ].map((kpi) => (
                    <div
                      key={kpi.label}
                      className="rounded-xl border border-[#2C363E] bg-gradient-to-b from-[#1C2227] to-[#1A1F24] px-4 py-4 shadow-[0_2px_12px_rgba(0,0,0,0.2)] hover:border-[#3A4852] transition-colors"
                    >
                      <p className="text-[11px] font-semibold text-[#869AA9] uppercase tracking-wider">
                        {kpi.label}
                      </p>
                      <p className="font-sora text-xl sm:text-2xl font-bold text-white mt-2 tabular-nums tracking-tight">
                        {kpi.value}
                      </p>
                      <p className="text-[11px] text-[#5C6B76] mt-1">
                        {kpi.hint}
                      </p>
                    </div>
                  ))}
                </section>

                <section
                  className="rounded-2xl border border-[#2C363E] bg-gradient-to-b from-[#1C2227] to-[#181D22] p-5 sm:p-7 mb-8 shadow-[0_8px_32px_rgba(0,0,0,0.28)]"
                  aria-labelledby="deex-desk-currency-heading"
                >
                  <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
                    <div>
                      <h2
                        id="deex-desk-currency-heading"
                        className="text-[11px] font-bold text-[#869AA9] uppercase tracking-widest"
                      >
                        Total volume (USD)
                      </h2>
                      <p className="font-sora font-bold text-[#BEE1FB] text-2xl sm:text-4xl mt-2 tabular-nums">
                        ${formatAmount(data.totalAmountUSD)}
                      </p>
                      <p className="font-sora text-[13px] text-[#869AA9] mt-2 flex flex-wrap gap-x-3 gap-y-1">
                        <span>₦{formatAmount(data.totalAmountNGN)}</span>
                        <span className="text-[#3A4852]" aria-hidden>
                          ·
                        </span>
                        <span>
                          {data.totalCount.toLocaleString()} transactions
                        </span>
                        {data.currency ? (
                          <>
                            <span className="text-[#3A4852]" aria-hidden>
                              ·
                            </span>
                            <span>Desk currency: {data.currency}</span>
                          </>
                        ) : null}
                      </p>
                    </div>
                  </div>

                  <div className="border-t border-[#2C363E]/80 pt-6">
                    <h3 className="text-[12px] font-semibold text-[#E7EBEE] mb-1">
                      Currency mix
                    </h3>
                    <p className="text-[12px] text-[#5C6B76] mb-2">
                      Select a segment for amounts and counts.
                    </p>

                    {segments.length === 0 ? (
                      <p className="text-[13px] text-[#869AA9] py-6 text-center rounded-lg bg-[#141920]/80 border border-dashed border-[#2C363E]">
                        No currency distribution for this range.
                      </p>
                    ) : (
                      <>
                        <div
                          className="flex w-full gap-1 p-1 rounded-xl bg-[#0D1114] min-h-[44px] shadow-inner"
                          role="group"
                          aria-label="Currency mix. Select a segment for details."
                        >
                          {segments.map((s) => {
                            const isSel = selectedCurrency === s.currency;
                            return (
                              <button
                                key={s.currency}
                                type="button"
                                aria-pressed={isSel}
                                aria-label={`${s.currency}, ${s.pct.toFixed(
                                  2
                                )} percent. Show details.`}
                                onClick={() => setSelectedCurrency(s.currency)}
                                className={`group relative flex items-center justify-center min-h-[44px] min-w-0 first:rounded-l-lg last:rounded-r-lg rounded-md py-2 px-0.5 transition-all hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1114] focus-visible:z-10 ${
                                  isSel
                                    ? "ring-2 ring-white/90 ring-inset z-[1] scale-[1.02]"
                                    : ""
                                }`}
                                style={{
                                  flexGrow: Math.max(s.pct, 0.4),
                                  flexBasis: 0,
                                  backgroundColor: s.color,
                                }}
                              >
                                {s.showLabel ? (
                                  <span className="pointer-events-none truncate px-1 text-[11px] font-semibold text-white drop-shadow-md sm:text-[12px]">
                                    {s.pct < 0.1
                                      ? `${s.pct.toFixed(2)}%`
                                      : `${s.pct.toFixed(1)}%`}
                                  </span>
                                ) : (
                                  <span className="sr-only">
                                    {s.currency}: {s.pct.toFixed(2)}%
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                        <div className="mt-3 flex w-full gap-1 px-1">
                          {segments.map((s) => {
                            const isSel = selectedCurrency === s.currency;
                            return (
                              <div
                                key={`leg-${s.currency}`}
                                className="min-w-0 flex justify-center"
                                style={{
                                  flexGrow: Math.max(s.pct, 0.4),
                                  flexBasis: 0,
                                }}
                              >
                                <button
                                  type="button"
                                  aria-pressed={isSel}
                                  onClick={() =>
                                    setSelectedCurrency(s.currency)
                                  }
                                  className={`max-w-full truncate rounded-md px-1.5 py-1 text-[10px] sm:text-[11px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary_button ${
                                    isSel
                                      ? "text-white bg-[#2C363E]"
                                      : "text-[#869AA9] hover:text-[#C6D0D7]"
                                  }`}
                                >
                                  {s.currency}
                                </button>
                              </div>
                            );
                          })}
                        </div>

                        {selectedSegment && (
                          <div
                            className="mt-6 rounded-xl border border-[#2C363E] bg-[#141920]/95 p-4 sm:p-5 shadow-[inset_0_0_0_1px_rgba(44,54,62,0.5)]"
                            style={{
                              borderLeftWidth: 4,
                              borderLeftColor: selectedSegment.color,
                            }}
                            role="region"
                            aria-live="polite"
                            aria-label={`Details for ${selectedSegment.currency}`}
                          >
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4 mb-4">
                              <div className="min-w-0">
                                <h4 className="font-sora text-lg font-bold text-white tracking-tight">
                                  {selectedSegment.currency}
                                </h4>
                                {selectedSegment.currency === "Other" ? (
                                  <p className="text-[12px] text-[#869AA9] mt-1 leading-snug">
                                    Combined smaller assets. Native amount sums
                                    mixed units across coins.
                                  </p>
                                ) : (
                                  <p className="text-[12px] text-[#5C6B76] mt-1">
                                    Amounts and counts for this asset in the
                                    selected range.
                                  </p>
                                )}
                              </div>
                            </div>

                            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div className="rounded-lg bg-[#1C2227]/80 border border-[#2C363E]/60 px-3 py-3">
                                <dt className="text-[10px] font-semibold text-[#5C6B76] uppercase tracking-wide">
                                  Native amount
                                </dt>
                                <dd className="text-[15px] font-semibold text-[#E7EBEE] tabular-nums mt-1 break-all">
                                  {formatAmount(selectedSegment.amount)}{" "}
                                  <span className="text-[#869AA9] font-medium">
                                    {selectedSegment.currency}
                                  </span>
                                </dd>
                              </div>
                              <div className="rounded-lg bg-[#1C2227]/80 border border-[#2C363E]/60 px-3 py-3">
                                <dt className="text-[10px] font-semibold text-[#5C6B76] uppercase tracking-wide">
                                  USD notional
                                </dt>
                                <dd className="text-[15px] font-semibold text-[#BEE1FB] tabular-nums mt-1">
                                  ${formatAmount(selectedSegment.usdAmount)}
                                </dd>
                              </div>
                              <div className="rounded-lg bg-[#1C2227]/80 border border-[#2C363E]/60 px-3 py-3">
                                <dt className="text-[10px] font-semibold text-[#5C6B76] uppercase tracking-wide">
                                  NGN amount
                                </dt>
                                <dd className="text-[15px] font-semibold text-[#E7EBEE] tabular-nums mt-1">
                                  ₦{formatAmount(selectedSegment.ngnAmount)}
                                </dd>
                              </div>
                              <div className="rounded-lg bg-[#1C2227]/80 border border-[#2C363E]/60 px-3 py-3">
                                <dt className="text-[10px] font-semibold text-[#5C6B76] uppercase tracking-wide">
                                  Transaction count
                                </dt>
                                <dd className="text-[15px] font-semibold text-white tabular-nums mt-1">
                                  {selectedSegment.count.toLocaleString()}
                                </dd>
                              </div>
                            </dl>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </section>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 mb-10">
                  <section aria-labelledby="deex-desk-status-heading">
                    <div className="flex items-baseline justify-between gap-2 mb-4">
                      <h2
                        id="deex-desk-status-heading"
                        className="text-[12px] font-bold text-[#869AA9] uppercase tracking-widest"
                      >
                        Status
                      </h2>
                      <span className="text-[11px] text-[#5C6B76]">
                        By transaction state
                      </span>
                    </div>
                    {statusEntries.length === 0 ? (
                      <p className="text-[13px] text-[#869AA9] rounded-lg border border-dashed border-[#2C363E] bg-[#141920]/50 px-4 py-6 text-center">
                        No status breakdown for this range.
                      </p>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {statusEntries.map(([key, count]) => {
                          const { bar, glow } = statusAccent(key);
                          return (
                            <div
                              key={key}
                              className="rounded-xl border border-[#2C363E] bg-[#1C2227] px-4 py-3.5 transition hover:border-[#3A4852] hover:shadow-[0_4px_20px_rgba(0,0,0,0.15)]"
                              style={{
                                boxShadow: `inset 0 0 0 1px ${glow}`,
                                borderLeftWidth: 3,
                                borderLeftColor: bar,
                              }}
                            >
                              <p className="text-[11px] text-[#869AA9] mb-1.5 leading-tight">
                                {formatStatusLabel(key)}
                              </p>
                              <p className="font-sora text-xl font-bold text-white tabular-nums">
                                {count.toLocaleString()}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </section>

                  {subtypeEntries.length > 0 && (
                    <section aria-labelledby="deex-desk-subtype-heading">
                      <div className="flex items-baseline justify-between gap-2 mb-4">
                        <h2
                          id="deex-desk-subtype-heading"
                          className="text-[12px] font-bold text-[#869AA9] uppercase tracking-widest"
                        >
                          Subtypes
                        </h2>
                        <span className="text-[11px] text-[#5C6B76]">
                          Mix of {subtypeTotal.toLocaleString()} classified
                        </span>
                      </div>
                      <div className="space-y-3">
                        {subtypeEntries.map(([key, count]) => {
                          const pct =
                            subtypeTotal > 0 ? (count / subtypeTotal) * 100 : 0;
                          const { bar, glow } = subtypeAccent(key);
                          return (
                            <div
                              key={key}
                              className="rounded-xl border border-[#2C363E] bg-[#1C2227] p-4 transition hover:border-[#3A4852]"
                              style={{
                                boxShadow: `inset 0 0 0 1px ${glow}`,
                              }}
                            >
                              <div className="flex items-center justify-between gap-3 mb-2">
                                <span className="text-[13px] font-semibold text-[#E7EBEE]">
                                  {humanizeSubtypeKey(key)}
                                </span>
                                <span className="text-[13px] tabular-nums text-[#AEBCC6]">
                                  <span className="font-sora font-bold text-white">
                                    {count.toLocaleString()}
                                  </span>
                                </span>
                              </div>
                              <div className="h-2 rounded-full bg-[#0D1114] overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all duration-500 ease-out"
                                  style={{
                                    width: `${Math.max(pct, 0)}%`,
                                    backgroundColor: bar,
                                    boxShadow: `0 0 12px ${glow}`,
                                  }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </section>
                  )}
                </div>
              </>
            )}

            {!dateRangeInvalid && (
              <>
                <div className="w-full flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mt-4 mb-3">
                  <h4 className="text-white font-sora font-semibold text-[12px] shrink-0">
                    Activity
                  </h4>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    <button
                      type="button"
                      onClick={() => setExportOpen(true)}
                      className="flex items-center justify-center gap-2 cursor-pointer opacity-90 hover:opacity-100"
                      aria-label="Export OTC summaries and transactions"
                    >
                      <BiExport className="text-white text-lg" aria-hidden />
                      <span className="uppercase font-sora font-semibold text-[#869AA9] text-[10px] mt-0.5">
                        Export
                      </span>
                    </button>
                  </div>
                </div>
                <div className="mt-2 mb-10">
                  <h3 className="text-[12px] font-bold text-[#869AA9] uppercase tracking-widest mb-1">
                    OTC activity
                  </h3>
                  <p className="text-[12px] text-[#5C6B76] mb-3">
                    Deposits and payouts are listed separately. The summaries
                    above always cover both.
                  </p>

                  <div
                    className="mb-4 flex flex-wrap gap-2"
                    role="tablist"
                    aria-label="OTC subtype filter"
                  >
                    {OTC_SUBTYPE_FILTERS.map((opt) => {
                      const active = otcSubtype === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          role="tab"
                          aria-selected={active}
                          onClick={() => setOtcSubtype(opt.value)}
                          className={
                            active
                              ? "rounded-full border border-secondary_button bg-secondary_button/15 px-4 py-2 text-[12px] font-bold text-secondary_button"
                              : "rounded-full border border-[#2C363E] bg-[#161E25]/80 px-4 py-2 text-[12px] font-semibold text-[#AEBCC6] transition hover:border-[#3A4852] hover:text-white"
                          }
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>

                  <div className="mb-4">
                    <input
                      type="search"
                      value={tableSearch}
                      onChange={(e) => setTableSearch(e.target.value)}
                      placeholder="Search transactions"
                      className="h-10 w-full rounded-lg border border-[#2C363E] bg-[#161E25]/80 px-3 text-[13px] text-[#E7EBEE] outline-none transition focus:border-secondary_button focus:ring-1 focus:ring-secondary_button/40"
                    />
                  </div>

                  {tableFetching && !tablePending && (
                    <p className="text-[12px] text-[#869AA9] mb-2">
                      Updating list…
                    </p>
                  )}

                  {tableError && (
                    <div className="rounded-xl border border-red-500/35 bg-red-500/[0.08] px-4 py-3 text-[13px] text-red-100/95 mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <span>
                        {tableQueryError?.response?.data &&
                        typeof tableQueryError.response.data === "object" &&
                        "message" in tableQueryError.response.data
                          ? String(
                              (
                                tableQueryError.response.data as {
                                  message?: string;
                                }
                              ).message
                            )
                          : "Could not load OTC transactions."}
                      </span>
                      <button
                        type="button"
                        onClick={() => refetchTable()}
                        className="shrink-0 rounded-lg bg-red-500/20 px-3 py-1.5 text-[12px] font-semibold text-red-200 border border-red-500/40"
                      >
                        Retry
                      </button>
                    </div>
                  )}

                  {tablePending && !tableError && (
                    <div className="flex justify-center py-16 rounded-xl border border-[#2C363E] bg-[#161E25]/40">
                      <TailSpin color="#0B75C2" height={36} width={36} />
                    </div>
                  )}

                  {!tablePending && !tableError && (
                    <DataTable<DeexDeskOtcTableRow>
                      columns={otcColumns}
                      data={tableRows}
                      currentPage={tablePage}
                      setCurrentPage={setTablePage}
                      totalPage={tableTotalPages}
                      count={tableTotalCount}
                      limit={OTC_TABLE_LIMIT}
                      emptyContent={`No OTC ${
                        otcSubtype === "otc-payout" ? "payouts" : "deposits"
                      } for this range.`}
                      className="rounded-xl border border-[#2C363E] overflow-hidden"
                      fixedBodyHeight="min(560px, 52vh)"
                    />
                  )}
                </div>

                {detailOpen && detailTransaction && (
                  <OtcDetailModal
                    transaction={detailTransaction}
                    onClose={() => {
                      setDetailOpen(false);
                      setDetailTransaction(null);
                    }}
                    onCompleteSuccess={() => {
                      void refetch();
                      void refetchTable();
                    }}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <AdminTransactionsExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title="Deex Desk (OTC)"
        exportContext={{ type: "otc", otcSubtype }}
      />
    </div>
  );
};

export default DeexDesk;
